import { useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { App, AppState } from '@capacitor/app';
import { Browser } from '@capacitor/browser';
import { Capacitor } from '@capacitor/core';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import { supabase } from '../integrations/supabase/client';
import { isOAuthInProgress, setOAuthInProgress } from '../lib/oauthState';

const LAUNCH_URL_KEY = 'petius_processed_launch_url';

// Close the in-app browser without ever blocking navigation.
const closeBrowserInBackground = () => {
  Promise.race([
    Browser.close(),
    new Promise((resolve) => setTimeout(resolve, 1500)),
  ]).catch(() => {});
};

/**
 * Single owner of the native OAuth return (deep link) and shared-content links.
 */
export const DeepLinkHandler = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const isProcessing = useRef(false);
  const lastUrl = useRef<{ url: string; at: number }>({ url: '', at: 0 });
  const pathRef = useRef(location.pathname);
  pathRef.current = location.pathname;
  const tRef = useRef(t);
  tRef.current = t;

  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return;

    // The /auth page performs its own post-login redirect (provider/ambassador rules).
    const goToFeed = () => {
      if (pathRef.current !== '/auth') navigate('/feed', { replace: true });
    };

    const finishOAuth = () => {
      setOAuthInProgress(false);
      closeBrowserInBackground();
      goToFeed();
    };

    const handleOAuthCallback = async (urlObj: URL) => {
      const params = new URLSearchParams(urlObj.hash?.substring(1) || urlObj.search);
      const query = new URLSearchParams(urlObj.search);
      const accessToken = params.get('access_token');
      const refreshToken = params.get('refresh_token');
      const code = params.get('code') || query.get('code');
      const errorDescription = params.get('error_description') || query.get('error_description');

      if (errorDescription) {
        console.error('❌ OAuth error:', errorDescription);
        setOAuthInProgress(false);
        closeBrowserInBackground();
        toast.error(tRef.current('authCallback.sessionError'));
        return;
      }

      isProcessing.current = true;
      try {
        if (accessToken && refreshToken) {
          const { data: { session } } = await supabase.auth.getSession();
          if (session?.access_token !== accessToken) {
            const { error } = await supabase.auth.setSession({ access_token: accessToken, refresh_token: refreshToken });
            if (error) throw error;
          }
          finishOAuth();
        } else if (code) {
          const { error } = await supabase.auth.exchangeCodeForSession(code);
          if (error) throw error;
          finishOAuth();
        }
      } catch (error) {
        console.error('❌ OAuth session error:', error);
        setOAuthInProgress(false);
        toast.error(tRef.current('authCallback.codeExchangeError'));
      } finally {
        isProcessing.current = false;
      }
    };

    const processUrl = async (url: string) => {
      const now = Date.now();
      if (lastUrl.current.url === url && now - lastUrl.current.at < 3000) return;
      lastUrl.current = { url, at: now };

      try {
        const urlObj = new URL(url);
        const host = urlObj.hostname || '';
        const path = urlObj.pathname || '';

        if (host === 'callback' || path.includes('/callback/')) {
          if (isProcessing.current) return;
          await handleOAuthCallback(urlObj);
          return;
        }

        if (host === 'post' || host === 'posts' || path.includes('/post/') || path.includes('/posts/')) {
          const postId = path.replace(/^\/+/, '').split('/').pop();
          if (postId) navigate(`/post/${postId}`, { replace: true });
          return;
        }

        if (host === 'pet' || path.includes('/pet/')) {
          const petId = path.replace(/^\/+/, '').split('/').pop();
          if (petId) navigate(`/pet/${petId}`, { replace: true });
        }
      } catch (error) {
        console.error('❌ Error parsing deep link:', error);
      }
    };

    // Cold start: process the launch URL only once per app session.
    App.getLaunchUrl().then((result) => {
      const url = result?.url;
      if (!url) return;
      try {
        if (sessionStorage.getItem(LAUNCH_URL_KEY) === url) return;
        sessionStorage.setItem(LAUNCH_URL_KEY, url);
      } catch {}
      processUrl(url);
    }).catch(() => {});

    const urlListener = App.addListener('appUrlOpen', (data: { url: string }) => processUrl(data.url));

    // Fallback when the deep link didn't arrive but the session exists.
    const checkSession = async () => {
      if (!isOAuthInProgress() || isProcessing.current) return;
      const { data: { session } } = await supabase.auth.getSession();
      if (session) finishOAuth();
    };

    const stateListener = App.addListener('appStateChange', (state: AppState) => {
      if (state.isActive) setTimeout(checkSession, 500);
    });

    const browserListener = Browser.addListener('browserFinished', () => {
      setTimeout(async () => {
        await checkSession();
        if (!isProcessing.current) setOAuthInProgress(false);
      }, 800);
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange((event) => {
      if (event === 'SIGNED_OUT') {
        isProcessing.current = false;
        setOAuthInProgress(false);
      }
    });

    return () => {
      urlListener.then((l) => l.remove()).catch(() => {});
      stateListener.then((l) => l.remove()).catch(() => {});
      browserListener.then((l) => l.remove()).catch(() => {});
      subscription.unsubscribe();
    };
  }, [navigate]);

  return null;
};
